import { Loader2, Pencil, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { TutorResponse } from '@/api/generated/model';
import {
  useTutoresControllerListar,
  useTutoresControllerObter,
} from '@/api/generated/tutores/tutores';
import { TutorFormDialogo } from '@/features/tutores/components/TutorFormDialogo';
import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { formatarParaExibicao } from '@/shared/lib/telefone';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Button } from '@/shared/ui/button';
import { ComboBox, type OpcaoCombo } from '@/shared/ui/combobox';
import { toast } from '@/shared/ui/sonner';

/** `''` = sem tutor. É o mesmo valor que o DTO recebe como `null`. */
export const SEM_TUTOR = '';

interface Props {
  /** Vai no gatilho, para o `<Label htmlFor>` do `<Campo>` alcançar. */
  id: string;
  /** `''` quando o paciente não tem tutor. */
  valor: string;
  aoEscolher: (tutorId: string) => void;
  /**
   * Nome do tutor JÁ vinculado, na edição.
   *
   * A busca é do servidor e troca a lista inteira: sem este nome, abrir um
   * paciente e digitar outro termo deixaria o campo em branco como se o vínculo
   * tivesse sido apagado.
   */
  nomeAtual?: string | null;
  /**
   * Id do tutor JÁ vinculado ao paciente (o que está gravado). O lápis só
   * aparece enquanto o campo mostra este tutor.
   */
  tutorVinculadoId?: string | null;
  /** Só consulta o servidor com o diálogo aberto. */
  habilitado?: boolean;
}

/**
 * Escolhe o tutor do paciente — com busca no servidor e cadastro no lugar.
 *
 * ⚠️ **A busca vai ao SERVIDOR, não filtra o que já veio.** A página é de 100 e
 * uma clínica com mais tutores do que isso teria tutor inalcançável no
 * formulário — que é exatamente o caminho pelo qual paciente nasce sem dono. O
 * servidor procura em nome, documento, e-mail e telefone
 * (`tutores.service.ts`), e é por isso que o `detalhe` de cada opção mostra o
 * contato: quem digita um telefone precisa ver por que aquela linha casou.
 *
 * ⚠️ **O `+` existe porque a tela de tutores saiu do menu.** O cadastro de tutor
 * é sempre disparado no meio de outra coisa (cadastrar o animal); mandar a
 * pessoa a outra tela e voltar perdia o formulário do paciente pela metade.
 *
 * ⚠️ **O lápis também existe por isso.** Sem a tela de tutores no menu, este é
 * o único lugar onde se corrige o cadastro do responsável. Ele só aparece para
 * o tutor VINCULADO (gravado no paciente): paciente sem responsável, ou um
 * responsável recém-escolhido e ainda não salvo, não tem o que editar.
 */
export function SeletorDeTutor({
  id,
  valor,
  aoEscolher,
  nomeAtual,
  tutorVinculadoId,
  habilitado = true,
}: Props) {
  const { pode } = usePermissoes();

  const [busca, setBusca] = useState('');
  const buscaAdiada = useDebounce(busca);
  const [cadastrando, setCadastrando] = useState(false);
  const [editando, setEditando] = useState(false);
  // O nome do escolhido acompanha a escolha, e não a lista: o tutor recém-criado
  // e o tutor de outra página da busca precisam aparecer no gatilho do mesmo
  // jeito. Semeado com o vínculo que veio do servidor.
  const [nomeEscolhido, setNomeEscolhido] = useState<string | null>(nomeAtual ?? null);

  const { data, isFetching } = useTutoresControllerListar(
    {
      pagina: 1,
      porPagina: 100,
      ordenarPor: 'nome',
      ordem: 'asc',
      ...(buscaAdiada.trim() ? { busca: buscaAdiada.trim() } : {}),
    },
    { query: { enabled: habilitado } },
  );

  // O cadastro completo vem do DETALHE, e não da lista: o tutor já vinculado
  // pode não estar na página da busca, e a lista só é base para a escolha.
  const mostraVinculado = Boolean(tutorVinculadoId) && valor === tutorVinculadoId;
  const podeEditar = mostraVinculado && pode('tutores.editar') && pode('tutores.visualizar');
  const emEdicao = useTutoresControllerObter(valor, {
    query: { enabled: editando && mostraVinculado },
  });

  useEffect(() => {
    if (!editando || !emEdicao.isError) return;
    toast.error('Não foi possível carregar o responsável.');
    setEditando(false);
  }, [editando, emEdicao.isError]);

  const opcoes: OpcaoCombo[] = [
    { valor: SEM_TUTOR, rotulo: 'Sem responsável cadastrado' },
    ...(data?.itens ?? []).map((tutor) => ({
      valor: tutor.id,
      rotulo: tutor.nome,
      ...(contato(tutor) ? { detalhe: contato(tutor) } : {}),
    })),
  ];

  function escolher(tutorId: string) {
    aoEscolher(tutorId);
    setNomeEscolhido(
      tutorId === SEM_TUTOR
        ? null
        : ((data?.itens ?? []).find((t) => t.id === tutorId)?.nome ?? null),
    );
  }

  return (
    <>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <ComboBox
            id={id}
            className={CLASSE_CONTROLE}
            valor={valor}
            aoEscolher={escolher}
            opcoes={opcoes}
            {...(nomeEscolhido ? { rotuloSelecionado: nomeEscolhido } : {})}
            placeholder="Sem responsável cadastrado"
            placeholderBusca="Buscar por nome, documento, e-mail ou telefone..."
            vazio="Nenhum responsável encontrado. Use o + para cadastrar."
            aoBuscar={setBusca}
            carregando={isFetching}
            aria-label="Responsável do paciente"
          />
        </div>

        {podeEditar && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label="Editar responsável"
            title="Editar responsável"
            disabled={editando}
            onClick={() => setEditando(true)}
          >
            {editando && !emEdicao.data ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Pencil className="w-4 h-4" />
            )}
          </Button>
        )}

        {pode('tutores.criar') && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label="Cadastrar novo responsável"
            title="Cadastrar novo responsável"
            onClick={() => setCadastrando(true)}
          >
            <UserPlus className="w-4 h-4" />
          </Button>
        )}
      </div>

      <TutorFormDialogo
        aberto={cadastrando}
        aoFechar={() => setCadastrando(false)}
        // Vincula o tutor novo na hora: foi para vinculá-lo que o cadastro
        // começou.
        aoCriar={(tutor) => {
          aoEscolher(tutor.id);
          setNomeEscolhido(tutor.nome);
        }}
      />

      <TutorFormDialogo
        aberto={editando && Boolean(emEdicao.data)}
        aoFechar={() => setEditando(false)}
        {...(emEdicao.data ? { tutor: emEdicao.data } : {})}
        aoAtualizar={(tutor) => setNomeEscolhido(tutor.nome)}
      />
    </>
  );
}

/** A linha de apoio da opção — o que desempata dois "MARIA" na lista. */
function contato(tutor: TutorResponse): string {
  return [tutor.documento, formatarParaExibicao(tutor.telefone), tutor.email]
    .filter(Boolean)
    .join(' · ');
}
